/**
 * Reading the `PLATFORM_ROLE` catalogue, outside any screen (f3.2, f8.3).
 *
 * f3.2 kept the lookup TYPE code in the Users screen's view module, which was
 * right while the Users screen was the only thing that needed it. f8.3 mounts
 * the invite dialog from the Data import screen, so the constant and the two
 * readings of the catalogue move here -- one module, imported by both, rather
 * than the import screen reaching into `routes/users/`.
 *
 * **Nothing here decides a role.** These are lookups into f2.2's live
 * catalogue by the codes the platform registered; a role inserted in a later
 * phase is offered with no change to this file (BR-9), and a role removed from
 * the catalogue simply is not found.
 *
 * `UNASSIGNED_ROLE_CODE` is a literal, and it is deliberately HERE rather than
 * in a component: BR-F4.2-4 says an imported or invited account lands with
 * zero permissions, so the import must be able to say which row that is, and
 * the specification's rule is that it is read from the live catalogue and
 * never hardcoded *in a component*. This is a pure module, and the id it
 * yields is always the catalogue's.
 */

import type { LookupCatalog, LookupValue } from '@/api/client';

/** The lookup type f2.2 registered for platform roles. Not a role code. */
export const PLATFORM_ROLE_TYPE = 'PLATFORM_ROLE';

/**
 * The role that grants nothing.
 *
 * f1.3 shipped three platform roles plus this one. An account holding it can
 * sign in and see nothing, which is exactly what an imported role holder
 * should be until an Admin decides otherwise.
 */
export const UNASSIGNED_ROLE_CODE = 'UNASSIGNED';

/** Every platform role the catalogue carries, in registry order. */
export function platformRolesFrom(
  catalog: LookupCatalog | undefined,
): readonly LookupValue[] {
  return (
    catalog?.types.find((type) => type.code === PLATFORM_ROLE_TYPE)?.values ?? []
  );
}

/**
 * The id of the `UNASSIGNED` row, or `undefined` if the catalogue has no such
 * row or has deactivated it.
 *
 * `undefined` rather than a fallback to the first role: pre-selecting whatever
 * happens to sort first would be the import guessing a platform role, which
 * BR-F4.2-4 forbids. An unprefilled picker is the correct degradation.
 */
export function unassignedRoleId(
  roles: readonly LookupValue[],
): string | undefined {
  return roles.find((role) => role.code === UNASSIGNED_ROLE_CODE && role.is_active)
    ?.id;
}
